import React from 'react'
import styled, {css, keyframes} from 'styled-components/macro'
import {getColor} from "@startlibs/utils"
import {AttachmentIcon} from "../AttachmentBoxStyles"
import {desaturate, lighten, darken, transparentize} from "polished"
import {DEVICE, DISK} from "../../enums/UploaderStepsManagement"

export const detailsExpand = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// ── Card shell ───────────────────────────────────────────────────────────────

export const CardShell = styled.div`
  display: flex;
  align-items: stretch;
  border-radius: 5px;
  margin-top: 0.75rem;
  border: 1px solid ${props => transparentize(0.75, darken(0.012, desaturate(0.4, getColor('main')(props))))};
  overflow: hidden;
  position: relative;
  outline: none;
  cursor: default !important;
  ${props => props.failedUpload && css`
    border-color: #e09f9f;
  `}
  ${props => props.unavailable && css`
    border: 1px solid rgba(0,0,0,0.15);
  `}
  ${props => props.isSelected && css`
    border-color: ${getColor('main')};
    box-shadow: inset 0 0 0 1px ${getColor('main')};
  `}
  ${props => props.isSelectMode && !props.disabledColor && css`
    cursor: pointer !important;
  `}
  ${props => (props.mode === DEVICE || props.mode === DISK) && css`
    border-radius: 0;
    margin-top: 0;
    border: 0;
    border-bottom: 1px solid lightgrey;
  `}
  ${props => props.compactMinified && css`
    border-bottom-color: rgba(0,0,0,0.12);
  `}
`

export const RowContent = styled.div`
  flex: 1;
  min-width: 0;
`

// ── Info panel (top section) ─────────────────────────────────────────────────

export const InfoPanel = styled.div`
  background: ${props => getColor('recordCardBg')(props)};
  display: flex;
  align-items: stretch;
  padding: 0;
  min-height: 62px;
  position: relative;
  ${props => props.unavailable && css`
    background: ${getColor('gray240')};
  `}
  ${props => props.failedUpload && css`
    background: ${props => lighten(0.51, props.theme.colors.alert)};
  `}
  ${props => props.isSelected && css`
    background: ${props => desaturate(0.3, lighten(0.475, getColor("main")(props)))};
  `}
  ${props => (props.mode === DEVICE || props.mode === DISK) && css`
    background: ${props => getColor('recordCardBg')(props)};
    padding: 8px 8px 8px 52px;
  `}
  ${props => props.compactMinified && css`
    min-height: 44px;
    padding: 4px 8px 4px 42px;
  `}
`

// ── Case info (left column inside info panel) ────────────────────────────────

export const CaseInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 13px 13px 10px;
  flex: 1;
  min-width: 0;
  ${props => props.compactMinified && css`
    align-items: center;
    gap: 8px;
    padding: 0px;
  `}
  ${props => props.selectingDM && css`
    align-items: center;
  `}
`

export const RecordIconWrap = styled.div`
  flex-shrink: 0;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 2px;
  ${props => props.compactMinified && css`
    width: 20px;
    align-items: center;
    justify-content: center;
    padding-top: 0;
  `}
`

export const RecordIcon = styled(AttachmentIcon)`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 24px;
  width: 24px;
  top: 14px;
  ${props => (props.mode === DEVICE || props.mode === DISK) && css`
    top: 22px;
  `}
  ${props => props.selectingDM && css`
    top: 18px;
  `}
  left: auto;
  text-align: center;
  flex-shrink: 0;
  ${props => props.compactMinified && css`
    font-size: 20px;
    width: 20px;
  `}
`

export const BasicFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`

export const AddActionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`

export const DetailsInlineButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 20px;
  padding: 0;
  border: 0;
  color: ${props => getColor('main')(props)};
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  cursor: pointer;
  user-select: none;
  align-self: flex-end;
  margin-top: auto;
  ${props => props.disabled && css`
    opacity: 0.45;
    pointer-events: none;
    cursor: not-allowed;
  `}
  :hover {
    text-decoration: ${props => props.disabled ? 'none' : 'underline'};
  }
`

// ── Info panel right side (actions + Details toggle) ─────────────────────────

export const InfoPanelRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  padding: 16px 16px 16px 0;
  flex-shrink: 0;
  ${props => props.compactMinified && css`
    gap: 4px;
    justify-content: center;
    padding: 6px 8px 6px 0;
  `}
`

// ── Expanded details panel ───────────────────────────────────────────────────

export const DetailsPanelOuter = styled.div`
  display: grid;
  grid-template-rows: ${props => props.isOpen ? '1fr' : '0fr'};
  transition: grid-template-rows 300ms ease-out;
`

export const DetailsPanelInner = styled.div`
  overflow: hidden;
`

export const DetailsPanel = styled.div`
  border-top: 1px solid ${props => desaturate(0.667, lighten(0.488, getColor('main')(props)))};
  background: linear-gradient(180deg, ${props => darken(0.025, getColor('recordDetailsBg')(props))} 0%, ${props => getColor('recordDetailsBg')(props)} 40.43%);
  padding: 16px 24px 16px 44px;
  display: flex;
  gap: 32px;
`

export const DetailsLeftCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const DetailsRightCol = styled.div`
  flex-shrink: 0;
  width: 176px;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`

export const DetailsHeading = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgb(45,45,45);
`

export const DetailsText = styled.div`
  font-size: 12px;
  color: rgba(0,0,0,0.7);
  line-height: 1.4;
`

export const DicomDetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const DicomDetailLabel = styled.div`
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

export const DicomDetailValue = styled.div`
  color: var(--Neutrals-Pure-black, #000);
  font-family: "Open Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

// ── Author strip (bottom) ────────────────────────────────────────────────────

export const AuthorStrip = styled.div`
  background: ${props => getColor('recordCardBg')(props)};
  border-top: 1px solid ${props => transparentize(0.75, darken(0.012, desaturate(0.4, getColor('main')(props))))};
  display: flex;
  gap: 2px;
  padding: 6px 16px 8px 44px;
  font-size: 12px;
  color: rgb(60,60,60);
  ${props => props.unavailable && css`
    background: ${getColor('gray240')};
    color: rgba(0,0,0,0.5);
    border-top: 1px solid rgba(0,0,0,0.15);
  `}
`

// ── Mover (drag handle, left side) ──────────────────────────────────────────

export const MoverHandle = styled.div`
  flex-shrink: 0;
  width: 20px;
  align-self: stretch;
  border-right: 1px solid ${props => desaturate(0.667, lighten(0.488, getColor('main')(props)))};
  background: ${props => getColor('recordDragHandleBg')(props)};
  color: ${props => transparentize(0.5, getColor('main')(props))};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: grab;
  :hover {
    opacity: 0.75;
  }
`

// Inline SVG 6-dot grip icon (2×3 dots — standard drag handle)
export const GripIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
    <circle cx="3" cy="2.5" r="1.5" fill="currentColor"/>
    <circle cx="3" cy="7" r="1.5" fill="currentColor"/>
    <circle cx="3" cy="11.5" r="1.5" fill="currentColor"/>
    <circle cx="7" cy="2.5" r="1.5" fill="currentColor"/>
    <circle cx="7" cy="7" r="1.5" fill="currentColor"/>
    <circle cx="7" cy="11.5" r="1.5" fill="currentColor"/>
  </svg>
)

export const ChevronDownIcon = () => (
  <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden="true">
    <path d="M1 1l3.5 3L8 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const ChevronUpIcon = () => (
  <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden="true">
    <path d="M8 4L4.5 1 1 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// ── Progress / status overlay strip ─────────────────────────────────────────

export const ProgressWrap = styled.div`
  padding: 0 16px 12px 0;
  flex-shrink: 0;
`
